/**
 * Mirrors the allowlist ls-analytics-backend applies to free-text fields
 * (`utils/text_sanitization.py`). The server is the authority — it rejects the
 * request with a 422 — so this exists to tell the user which character is the
 * problem while they type, instead of after they submit.
 *
 * Keep the two in sync: unicode letters and digits, spaces and basic
 * punctuation are allowed; the characters markup, script and template syntax
 * are built out of (`< > { } $ " ` \ | ~ ^`) are not. Both sides compare the
 * NFC form of the value, so a decomposed accent is judged identically.
 */

export const SAFE_TEXT_MAX_LENGTH = 255

// \p{L}\p{N}_ rather than \w: JavaScript's \w is ASCII-only, so it would
// reject "Relatório" where Python's unicode-aware \w accepts it.
const SAFE_TEXT_RE = /^(?:[\p{L}\p{N}_]|[ \-.,;:/()[\]&'#!?@+%])*$/u
const UNSAFE_CHAR_RE = /(?![\p{L}\p{N}_]|[ \-.,;:/()[\]&'#!?@+%])[\s\S]/gu
const CONTROL_CHAR_RE = /[\x00-\x1f\x7f-\x9f]/

/**
 * Returns the distinct characters of `value` the server would reject.
 */
export function unsafeTextChars(value: string): Array<string> {
  return Array.from(new Set(value.normalize('NFC').match(UNSAFE_CHAR_RE) ?? []))
}

/**
 * Returns true when every character of `value` is in the allowlist.
 */
export function isSafeText(value: string): boolean {
  return SAFE_TEXT_RE.test(value.normalize('NFC'))
}

/**
 * Validates a free-text field the same way the server does.
 *
 * @param value The value typed by the user.
 * @param label Field name to use in the message, e.g. "Email subject".
 * @param maxLength Maximum length accepted by the server.
 * @return An error message, or `null` when the value is acceptable.
 */
export function getSafeTextError(
  value: string,
  label: string,
  maxLength: number = SAFE_TEXT_MAX_LENGTH
): string | null {
  if (!value) return null

  if (value.length > maxLength) {
    return `${label} must be at most ${maxLength} characters.`
  }

  if (CONTROL_CHAR_RE.test(value)) {
    return `${label} must not contain line breaks.`
  }

  if (!isSafeText(value)) {
    const rejected = unsafeTextChars(value).join(' ')

    return `${label} cannot contain ${rejected} — use letters, numbers, spaces and basic punctuation.`
  }

  return null
}
